import React, { useState } from 'react';
import { Board } from '@/stores/canvasStore';
import { Folder, X, Check, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface BoardPickerProps {
  boards: Board[];
  selectedBoardId: string | null;
  onSelectBoard: (boardId: string) => void;
  onRemoveFromBoard: () => void;
  onCreateBoard?: () => void;
}

export function BoardPicker({ 
  boards, 
  selectedBoardId, 
  onSelectBoard, 
  onRemoveFromBoard,
  onCreateBoard 
}: BoardPickerProps) {
  const [showPicker, setShowPicker] = useState(false);
  
  const selectedBoard = boards.find(b => b.id === selectedBoardId);
  // Filter out the home board for the picker
  const selectableBoards = boards.filter(b => b.parent_id !== null);

  return (
    <div className="border-t border-border p-4 bg-muted/50">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Folder className="w-5 h-5 text-muted-foreground" />
          
          {selectedBoardId && selectedBoard ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                In board:
              </span>
              <span 
                className="px-2 py-1 rounded-lg text-sm font-medium text-white"
                style={{ 
                  backgroundColor: selectedBoard.icon_color || '#6366F1' 
                }}
              >
                {selectedBoard.name}
              </span>
              <button
                onClick={onRemoveFromBoard}
                className="text-muted-foreground hover:text-destructive ml-1 transition-colors"
                title="Remove from board"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">
              Not in any board
            </span>
          )}
        </div>
        
        <Button
          onClick={() => setShowPicker(!showPicker)}
          size="sm"
        >
          {selectedBoardId ? 'Change Board' : 'Add to Board'}
        </Button>
      </div>
      
      {/* Board Picker Dropdown */}
      {showPicker && (
        <div className="mt-3 p-2 bg-background rounded-lg border border-border shadow-lg">
          <p className="text-xs text-muted-foreground px-2 py-1 mb-1">
            Select a board:
          </p>
          <div className="max-h-48 overflow-y-auto">
            {selectableBoards.map(board => (
              <button
                key={board.id}
                onClick={() => {
                  onSelectBoard(board.id);
                  setShowPicker(false);
                }}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors',
                  selectedBoardId === board.id 
                    ? 'bg-primary/10' 
                    : 'hover:bg-muted'
                )}
              >
                <div 
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: board.icon_color || '#6366F1' }}
                />
                <span className="text-sm text-foreground">
                  {board.name}
                </span>
                {selectedBoardId === board.id && (
                  <Check className="w-4 h-4 text-primary ml-auto" />
                )}
              </button>
            ))}
          </div>
          
          {/* Create New Board Option */}
          {onCreateBoard && (
            <div className="border-t border-border mt-2 pt-2">
              <button
                onClick={() => {
                  setShowPicker(false);
                  onCreateBoard();
                }}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-muted transition-colors"
              >
                <Plus className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  Create new board
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
